import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ConnectedProps } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faEnvelope, faEye, faLock, faPhone, faUser } from "@fortawesome/pro-regular-svg-icons";
import { connector } from "./redux";
import { ClavaContext } from "../../../../config/contexts";
import { translate } from "../../../../config/translator";
import { checkMail, isRegistered } from "../../../../config/utils";
import { normalizePhoneNumber } from "../../../../config/phone";
import { RECAPTCHA_ACTION_SIGNUP } from "../../../../config/constants";
import { useReCaptcha } from "../../../../hooks/useReCaptcha";
import { TelCodes } from "../../../../config/telCode";
import RoundedRectangle from "../../../components/RoundedRectangle";
import SegmentedTabs from "../../../components/SegmentedTabs";
import ClavaTextInput from "../../../components/ClavaTextInput";
import LeftSide from "../LeftSide";

const PHONE_PREFIXES = TelCodes.map(({ dialCode, name, image, code }) => ({
  key: dialCode,
  dialCode,
  name,
  image,
  code
}));

const Register: React.FC<ConnectedProps<typeof connector>> = ({
                                                                submit,
                                                                registerStatus,
                                                                user,
                                                                reset
                                                              }) => {
  const { l } = useContext(ClavaContext);
  const { redirectAfter } = useParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>("");
  const [givenName, setGivenName] = useState<string>("");
  const [familyName, setFamilyName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [telPrefix, setTelPrefix] = useState<string>(PHONE_PREFIXES[0]?.key ?? "+39");
  const [telLocal, setTelLocal] = useState<string>("");
  const [newsletter, setNewsletter] = useState<boolean>(false);
  const [agbLevel, setAgbLevel] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordRepeat, setPasswordRepeat] = useState<string>("");
  const [pwVisible, setPwVisible] = useState<boolean>(false);
  const [pwRepVisible, setPwRepVisible] = useState<boolean>(false);
  const [prefixOpen, setPrefixOpen] = useState<boolean>(false);
  const pwInput = useRef<HTMLInputElement>(null);
  const prefixDropdownRef = useRef<HTMLDivElement | null>(null);
  const { reCaptchaToken, refresh } = useReCaptcha(RECAPTCHA_ACTION_SIGNUP);
  useEffect(() => {
    reset();
  }, [reset]);
  const mailValid = useMemo(
    () => registerStatus !== "mailGiven" && registerStatus !== "mailInvalid",
    [registerStatus]
  );
  const telValid = useMemo(
    () => registerStatus !== "telGiven" && registerStatus !== "telInvalid",
    [registerStatus]
  );
  const pwValid = useMemo(
    () => registerStatus !== "pwNotSame" && registerStatus !== "pwNotValid",
    [registerStatus]
  );
  const pwRepValid = useMemo(
    () => registerStatus !== "pwNotSame",
    [registerStatus]
  );
  const selectedPrefix = useMemo(
    () => PHONE_PREFIXES.find((prefix) => prefix.key === telPrefix) ?? PHONE_PREFIXES[0],
    [telPrefix]
  );
  const canSubmit = useMemo(() => {
    const fieldsFilled = username.trim().length > 0
      && givenName.trim().length > 0
      && familyName.trim().length > 0
      && email.trim().length > 0
      && telLocal.trim().length > 0;
    const passwordsOk = password.length >= 8 && password === passwordRepeat;
    const emailOk = checkMail(email);
    return agbLevel && fieldsFilled && passwordsOk && emailOk;
  }, [agbLevel, username, givenName, familyName, email, telLocal, password, passwordRepeat]);
  useEffect(() => {
    if (isRegistered(user)) {
      setTimeout(() => {
        navigate(redirectAfter || "/profile");
      }, 500);
    }
  }, [user, navigate, redirectAfter]);

  useEffect(() => {
    if (!prefixOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        prefixDropdownRef.current &&
        !prefixDropdownRef.current.contains(event.target as Node)
      ) {
        setPrefixOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [prefixOpen]);

  const onTogglePWVisible = useCallback(() => {
    setPwVisible((v) => !v);
  }, []);
  const onTogglePWRepVisible = useCallback(() => {
    setPwRepVisible((v) => !v);
  }, []);
  const onToggleAgb = useCallback(() => {
    setAgbLevel((a) => !a);
  }, []);
  const onToggleNewsletter = useCallback(() => {
    setNewsletter((a) => !a);
  }, []);
  const onSubmit = useCallback(() => {
    if (!agbLevel) {
      return;
    }
    if (!reCaptchaToken) {
      refresh();
      return;
    }
    const sanitizedTelLocal = telLocal.replace(/\s+/g, "");
    const combinedTel = sanitizedTelLocal
      ? normalizePhoneNumber(`${telPrefix}${sanitizedTelLocal}`)
      : "";

    submit(
      username,
      givenName,
      familyName,
      email,
      password,
      passwordRepeat,
      combinedTel,
      newsletter,
      reCaptchaToken
    );
  }, [
    agbLevel,
    username,
    submit,
    givenName,
    familyName,
    email,
    password,
    passwordRepeat,
    telLocal,
    telPrefix,
    newsletter,
    reCaptchaToken,
    refresh
  ]);
  return (
    <div className="w-full px-6 xl:px-4 mx-auto xl:max-w-screen-xl">
      <div className="w-full flex flex-row items-start justify-between gap-6">
        <div className="flex-1 flex flex-col items-stretch justify-start">
          <RoundedRectangle
            hasTitle
            titleType="upLeft"
            titleKey="register"
            hasBorder
            className="bg-bg-secondary dark:bg-bg-secondary-dark border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark mb-4"
          >
            <div className="p-6 xl:p-4">
              {registerStatus !== "ok" && (
                <span className="text-red font-bold">
                  {translate(registerStatus, l)}
                </span>
              )}

              <div className="my-4 w-full max-w-96 min-w-72">
                <ClavaTextInput
                  onChange={(v) => setUsername(v)}
                  value={username}
                  type="text"
                  placeholder="maxmuster"
                  label={translate("username", l)}
                  iconL={faUser}
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="my-0 w-full max-w-96 min-w-72">
                  <ClavaTextInput
                    onChange={(v) => setGivenName(v)}
                    value={givenName}
                    type="text"
                    placeholder="Max"
                    label={translate("givenName", l)}
                    iconL={faUser}
                  />
                </div>
                <div className="my-0 w-full max-w-96 min-w-72">
                  <ClavaTextInput
                    onChange={(v) => setFamilyName(v)}
                    value={familyName}
                    type="text"
                    placeholder="Mustermann"
                    label={translate("familyName", l)}
                    iconL={faUser}
                  />
                </div>
              </div>

              <div className="my-4 w-full max-w-96 min-w-72">
                <ClavaTextInput
                  onChange={(v) => setEmail(v)}
                  value={email}
                  type="text"
                  error={!mailValid}
                  placeholder="max@mustermann.com"
                  label={translate("mailAddress", l)}
                  iconL={faEnvelope}
                />
              </div>
              <div className="my-4 w-full max-w-96 min-w-72">
                <label className="block text-xs font-semibold mb-1">
                  {translate("tel", l)}
                </label>
                <div className="flex gap-2 items-stretch">
                  <div
                    className="w-44 flex-shrink-0 relative"
                    ref={prefixDropdownRef}
                  >
                    <button
                      type="button"
                      className={`w-full h-11 px-2 border rounded-md bg-input-bg dark:bg-input-bg-dark text-left text-xs flex items-center justify-between gap-2 ${
                        !telValid
                          ? "border-red text-red"
                          : "border-gray-300 dark:border-gray-600 text-font dark:text-font-dark"
                      }`}
                      onClick={() => setPrefixOpen((open) => !open)}
                    >
                      <span className="truncate flex items-center gap-2">
                        <img
                          src={selectedPrefix.image}
                          alt={`${selectedPrefix.name} flag`}
                          className="w-5 h-3 object-cover rounded-sm border border-light-gray"
                        />
                        <span>{selectedPrefix.dialCode}</span>
                      </span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="w-3 h-3 opacity-70"
                      />
                    </button>
                    {prefixOpen && (
                      <div
                        className="absolute top-full left-0 mt-1 w-full max-h-80 overflow-y-auto rounded-md border border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark shadow-lg z-20">
                        {PHONE_PREFIXES.map((prefix) => (
                          <button
                            key={`${prefix.code}-${prefix.key}`}
                            type="button"
                            className={`w-full px-3 py-2 text-left text-xs hover:bg-primary-20 ${
                              prefix.key === telPrefix
                                ? "!bg-primary !text-white"
                                : "text-font dark:text-font-dark"
                            }`}
                            onClick={() => {
                              setTelPrefix(prefix.key);
                              setPrefixOpen(false);
                            }}
                          >
                            <span className="flex items-center gap-2">
                              <img
                                src={prefix.image}
                                alt={`${prefix.name} flag`}
                                className={`w-5 h-3 object-cover rounded-sm border ${
                                  prefix.key === telPrefix ? "border-primary-light" : "border-light-gray"
                                }`}
                              />
                              <span className="flex-1 truncate">{prefix.dialCode}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <ClavaTextInput
                      onChange={(v) => setTelLocal(v)}
                      value={telLocal}
                      type="text"
                      error={!telValid}
                      placeholder="0123456789"
                      iconL={faPhone}
                    />
                  </div>
                </div>
              </div>

              <div className="my-4 w-full max-w-96 min-w-72">
                <div className="relative">
                  <ClavaTextInput
                    onChange={(v) => setPassword(v)}
                    value={password}
                    type={pwVisible ? "text" : "password"}
                    error={!pwValid}
                    placeholder="password"
                    iconL={faLock}
                    innerRef={pwInput}
                  />
                  <button
                    className={`w-4 h-4 bottom-3.5 right-3 absolute ${pwVisible ? " !text-primary" : ""}`}
                    onClick={onTogglePWVisible}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                </div>
              </div>

              <div className="my-4 w-full max-w-96 min-w-72">
                <div className="relative">
                  <ClavaTextInput
                    onChange={(v) => setPasswordRepeat(v)}
                    value={passwordRepeat}
                    type={pwRepVisible ? "text" : "password"}
                    error={!pwRepValid}
                    placeholder="password"
                    iconL={faLock}
                  />
                  <button
                    className={`w-4 h-4 bottom-3.5 right-3 absolute ${pwRepVisible ? " !text-primary" : ""}`}
                    onClick={onTogglePWRepVisible}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                </div>
              </div>

              <div className="my-2 w-full max-w-2xl">
              <div className="flex flex-col gap-4 items-start justify-between xl:flex-row xl:items-center">
                  <label htmlFor="agbLevel" className="flex items-start gap-3 text-xs">
                    <input
                      type="checkbox"
                      id="agbLevel"
                      name="agbLevel"
                      checked={agbLevel}
                      onChange={onToggleAgb}
                    />
                    <span>
                      {translate("acceptAgb1", l)}
                      <a href="https://www.clava-sports.com/tos.html" className="nav-link inline">
                        {translate("tos", l)}
                      </a>
                      {translate("acceptAgb2", l)}
                      <a href="https://www.clava-sports.com/privacy.html" className="nav-link inline">
                        {translate("privacy", l)}
                      </a>
                      {translate("acceptAgb3", l)}
                    </span>
                  </label>
                  <label htmlFor="newsletter" className="flex items-center gap-3 text-xs">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={newsletter}
                      onChange={onToggleNewsletter}
                    />
                    <span>{translate("wantNewsletter", l)}</span>
                  </label>
                </div>
              </div>

              <div className="my-4 w-full max-w-96 min-w-72 flex flex-col gap-3">
                <SegmentedTabs
                  className="p-0 gap-2 w-full"
                  fullWidth
                  items={[{
                    key: "register",
                    label: translate("registerNow", l),
                    onClick: onSubmit,
                    disabled: !canSubmit
                  }]}
                  activeKey={canSubmit ? "register" : undefined}
                />
                <NavLink to="/login" className="text-primary underline">
                  {`${translate("alreadyRegistered", l)} ${translate("login", l)}`}
                </NavLink>
              </div>
            </div>
          </RoundedRectangle>
        </div>
        <div className="shrink-0 w-96 max-w-96 hidden xl:block">
          <LeftSide />
        </div>
      </div>
    </div>
  );
};
export default connector(Register);
